import * as Sentry from "@sentry/browser";
import { isViewAsTab } from "@helpers/viewAs/viewAsSession";
import store from "../../state/store";
import { getCorrelationIds } from "./requestCorrelation";

interface TrackedRequestInput {
  tag?: string;
  exception: any;
  message?: string;
  // Use scope Email for exceptions before user login.
  // Like the login itself or reset password.
  scopeEmail?: string;
  // Searchable key/value tags (e.g. request_id / client_request_id for
  // matching the backend log trail); undefined values are skipped.
  tags?: Record<string, string | undefined>;
  // Explicit grouping (e.g. per-endpoint axios failures) instead of
  // Sentry's stack-based default.
  fingerprint?: string[];
}

const getExceptionStatus = (exception: any): number | undefined => {
  const status = exception?.response?.status ?? exception?.status;
  return typeof status === "number" ? status : undefined;
};

// Scalar summary of the original exception. The raw object must never be
// attached to the event: axios errors carry config (with auth headers) and
// full response bodies.
const toExceptionSummary = (
  exception: any
): Record<string, string | number> => {
  const summary: Record<string, string | number> = {};
  if (typeof exception?.name === "string") summary.name = exception.name;
  if (typeof exception?.message === "string") {
    summary.message = exception.message;
  }
  if (
    typeof exception?.code === "string" ||
    typeof exception?.code === "number"
  ) {
    summary.code = exception.code;
  }
  const status = getExceptionStatus(exception);
  if (status !== undefined) summary.status = status;
  return summary;
};

// Sentry titles issues from the captured value's name/message, falling back
// to the top stack frame — meaningless in a minified bundle. When a
// human-readable message is supplied, normalise the value into an Error so
// the issue title is the message. Only classification-relevant scalars are
// copied across, never the full property bag.
const toCapturable = (exception: any, message: string): Error => {
  if (exception instanceof Error) {
    if (!exception.message) {
      exception.message = message;
    }
    return exception;
  }
  const synthetic = new Error(message);
  synthetic.name = "LoggedException";

  const carrier = synthetic as Error & Record<string, unknown>;
  if (exception?.code !== undefined) carrier.code = exception.code;
  const status = getExceptionStatus(exception);
  if (status !== undefined) carrier.status = status;

  return synthetic;
};

/**
 * Determines if an error should be logged to Sentry.
 * Filters out known noise (network failures, auth errors, validation errors).
 * Logs everything else to avoid missing potential bugs.
 */
const shouldLogException = (exception: any): boolean => {
  // Check if it's an Axios error
  if (exception?.isAxiosError) {
    const status = exception?.response?.status;
    const code = exception?.code;

    // Don't log network failures (status 0 or no response)
    // This is typically user connectivity issues, not bugs
    if (!exception?.response || status === 0) {
      return false;
    }

    // Don't log network error codes
    if (code === "ERR_NETWORK" || code === "ECONNABORTED") {
      return false;
    }

    // Don't log auth errors (401, 403) - expected behavior when
    // user is not logged in or doesn't have permission
    if (status === 401 || status === 403) {
      return false;
    }

    // Don't log validation errors (400) - typically user input errors
    if (status === 400) {
      return false;
    }

    // Log all other HTTP errors (404, 409, 422, 429, 5xx, etc.)
    // These could indicate actual bugs or issues worth investigating
    return true;
  }

  // Check for generic network error message
  if (exception?.message === "Network Error") {
    return false;
  }

  // Log non-Axios errors (could be actual bugs)
  return true;
};

export const logException = ({
  tag,
  exception,
  message,
  scopeEmail,
  tags,
  fingerprint,
}: TrackedRequestInput): void => {
  // Classify the original exception, not the message-titled wrapper below:
  // wrapping replaces the fields the noise filters match on, so expected
  // errors would slip through to Sentry.
  if (!shouldLogException(exception)) {
    return;
  }

  const state = store.store.getState();
  const { id } = state.auth;

  // Correlation ids come straight off the axios error, so every reported
  // API failure (any status) is linkable to its backend trail, not just
  // the interceptor's 5xx path.
  const axiosError = exception as {
    isAxiosError?: boolean;
    config?: Parameters<typeof getCorrelationIds>[0];
    response?: Parameters<typeof getCorrelationIds>[1];
  };
  const derivedTags = axiosError?.isAxiosError
    ? { ...getCorrelationIds(axiosError.config, axiosError.response), ...tags }
    : tags;

  // Everything is scoped per event: global tags would persist on the
  // isolation scope and stamp a later, unrelated error with this one's
  // request ids, sending triage to the wrong backend trail.
  Sentry.withScope((scope) => {
    // Identity is by id (the backend's auth_user_id), email only pre-login; a viewing tab sends a tag and no user.
    if (isViewAsTab()) {
      scope.setUser(null);
      scope.setTag("view_as", "true");
    } else if (scopeEmail) {
      scope.setUser({ email: scopeEmail });
    } else if (id) {
      scope.setUser({ id });
    }

    if (tag) {
      scope.setTag("request", tag);
    }

    Object.entries(derivedTags ?? {}).forEach(([key, value]) => {
      if (value !== undefined) {
        scope.setTag(key, value);
      }
    });

    if (fingerprint?.length) {
      scope.setFingerprint(fingerprint);
    }

    // A supplied message becomes the captured Error's title (via
    // toCapturable) rather than a separate captureMessage event — capturing
    // both minted twin issues for every failure.
    if (message) {
      scope.setContext("logException", {
        message,
        originalException: toExceptionSummary(exception),
      });
    }

    Sentry.captureException(
      message ? toCapturable(exception, message) : exception
    );
  });
};
